import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { billingService } from "../services/api";
import { useAuth } from "../context/AuthContext";
import "./PricingPage.css";

// Check if user is an early adopter (first 100 users)
const isEarlyAdopter = (user) => {
  // You can implement logic based on user ID, registration date, or a specific flag
  // For now, returning true - you can add your logic here
  return true; // TODO: Implement early adopter check
};

const PLAN_OPTIONS = [
  {
    key: "free",
    name: "Free Trial",
    price: "0€",
    cta: "Commencer",
    highlight: false,
    features: [
      { label: "5 articles / mois", included: true },
      { label: "Recherche avancée", included: false },
      { label: "Export CSV", included: false },
      { label: "Support prioritaire", included: false }
    ]
  },
  {
    key: "semi_pro",
    name: "Semi-pro",
    priceMonthly: "10€/mois",
    priceYearly: "99€/an",
    priceMonthlyAfter: "15€/mois",
    priceYearlyAfter: "149€/an",
    cta: "Essayer 7j gratuits",
    highlight: true,
    features: [
      { label: "Accès complet à la base d'articles", included: true },
      { label: "Recherche avancée", included: true },
      { label: "Export CSV", included: true },
      { label: "Support prioritaire", included: false }
    ]
  },
  {
    key: "pro",
    name: "Pro",
    priceMonthly: "15€/mois",
    priceYearly: "149€/an",
    priceMonthlyAfter: "20€/mois",
    priceYearlyAfter: "199€/an",
    cta: "S'abonner",
    highlight: false,
    features: [
      { label: "Accès complet à la base d'articles", included: true },
      { label: "Recherche avancée", included: true },
      { label: "Export CSV illimité", included: true },
      { label: "Support prioritaire", included: true }
    ]
  }
];

const STATUS_LABELS = {
  none: "Aucun abonnement actif",
  on_trial: "Essai en cours",
  active: "Abonnement actif",
  past_due: "Paiement en retard",
  paused: "Abonnement en pause",
  cancelled: "Abonnement annulé",
  expired: "Abonnement expiré"
};

function FeatureItem({ label, included }) {
  return (
    <li className={`feature-item${included ? " included" : " excluded"}`}>
      <span className={included ? "icon-check" : "icon-cross"}>{included ? "✓" : "✕"}</span>
      <span>{label}</span>
    </li>
  );
}

function PricingCard({ plan, isCurrent, isLoading, onSelect, isEarlyAdopter }) {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const disabled = isCurrent || isLoading;
  const label = isLoading ? "Redirection..." : isCurrent ? "Plan actuel" : plan.cta;

  const handleClick = () => {
    if (!disabled) {
      onSelect(plan.key, billingCycle);
    }
  };

  // Determine which price to show
  let displayPrice = plan.price;
  if (plan.priceMonthly && plan.priceYearly) {
    if (isEarlyAdopter) {
      displayPrice = billingCycle === 'monthly' ? plan.priceMonthly : plan.priceYearly;
    } else {
      displayPrice = billingCycle === 'monthly' ? plan.priceMonthlyAfter : plan.priceYearlyAfter;
    }
  }

  return (
    <div className={`pricing-card${plan.highlight ? " highlight" : ""}`}>
      {plan.highlight && isEarlyAdopter && (
        <div className="early-adopter-badge">🎉 Early Adopter Price</div>
      )}
      <h3>{plan.name}</h3>

      {plan.priceMonthly && plan.priceYearly && (
        <div className="billing-cycle-toggle">
          <button
            className={billingCycle === 'monthly' ? 'active' : ''}
            onClick={() => setBillingCycle('monthly')}
          >
            Mensuel
          </button>
          <button
            className={billingCycle === 'yearly' ? 'active' : ''}
            onClick={() => setBillingCycle('yearly')}
          >
            Annuel
          </button>
        </div>
      )}

      <div className="price">{displayPrice}</div>

      {plan.priceMonthly && !isEarlyAdopter && (
        <div className="price-after">
          Prix normal: {billingCycle === 'monthly' ? plan.priceMonthlyAfter : plan.priceYearlyAfter}
        </div>
      )}

      {billingCycle === 'yearly' && plan.priceYearly && (
        <div className="yearly-savings">
          Économisez {isEarlyAdopter ? '21€' : '31€'} par an
        </div>
      )}

      <ul className="features-list">
        {plan.features.map((feature, index) => (
          <FeatureItem key={index} {...feature} />
        ))}
      </ul>
      <button
        type="button"
        className={`cta-btn${disabled ? " disabled" : ""}`}
        onClick={handleClick}
        disabled={disabled}
      >
        {label}
      </button>
    </div>
  );
}

export default function PricingPage() {
  const { currentUser, isAuthenticated, updateUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activePlan, setActivePlan] = useState(currentUser?.plan || "free");
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const earlyAdopter = isAuthenticated && isEarlyAdopter(currentUser);

  useEffect(() => {
    if (currentUser?.plan && currentUser.plan !== activePlan) {
      setActivePlan(currentUser.plan);
    }
  }, [currentUser?.plan]);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      if (!isAuthenticated) {
        return;
      }
      setStatusLoading(true);
      try {
        const response = await billingService.getStatus();
        if (!cancelled && response?.success && response.user) {
          updateUser(response.user);
          setActivePlan(response.user.plan || "free");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Erreur lors de la récupération du statut Lemon Squeezy:", err);
        }
      } finally {
        if (!cancelled) {
          setStatusLoading(false);
        }
      }
    };
    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, updateUser]);

  const handleSelectPlan = async (planKey, billingCycle) => {
    setError(null);
    setMessage(null);

    if (!isAuthenticated) {
      setError("Veuillez vous connecter pour choisir un plan.");
      navigate("/login", { state: { from: "/pricing" } });
      return;
    }

    if (planKey === activePlan) {
      setMessage("Ce plan est déjà actif.");
      return;
    }

    setLoadingPlan(planKey);
    try {
      const response = await billingService.createCheckout(planKey, billingCycle);
      if (!response?.success) {
        throw new Error(response?.error || "Impossible de créer la session de paiement");
      }

      if (planKey === "free") {
        if (response.user) {
          updateUser(response.user);
          setActivePlan(response.user.plan || "free");
        } else {
          await refreshUser();
        }
        setMessage("Le plan Free est maintenant actif.");
      } else if (response.url) {
        window.location.href = response.url;
      } else {
        setMessage("Session de paiement générée. Vérifiez votre boîte mail.");
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Erreur lors de la création du paiement";
      setError(msg);
    } finally {
      setLoadingPlan(null);
    }
  };

  const activePlanInfo = PLAN_OPTIONS.find((plan) => plan.key === activePlan) || PLAN_OPTIONS[0];
  const statusLabel = currentUser?.subscriptionStatus ? STATUS_LABELS[currentUser.subscriptionStatus] : null;

  return (
    <div className="pricing-page">
      <h1>Tarifs</h1>
      <p className="pricing-desc">
        Choisissez le plan qui correspond à vos besoins. Sans engagement, annulation à tout moment.
      </p>

      {earlyAdopter && (
        <div className="early-adopter-banner">
          🎉 Félicitations ! Vous bénéficiez du prix Early Adopter (100 premiers utilisateurs)
        </div>
      )}

      {isAuthenticated && (
        <div className="pricing-status">
          <span>Plan actuel :</span> {activePlanInfo.name}
          {statusLabel ? ` • Statut : ${statusLabel}` : null}
          {statusLoading ? " • Mise à jour..." : null}
        </div>
      )}

      {message && <div className="pricing-message success">{message}</div>}
      {error && <div className="pricing-message error">{error}</div>}

      <div className="pricing-cards-container">
        {PLAN_OPTIONS.map((plan) => (
          <PricingCard
            key={plan.key}
            plan={plan}
            isCurrent={plan.key === activePlan}
            isLoading={loadingPlan === plan.key}
            onSelect={handleSelectPlan}
            isEarlyAdopter={earlyAdopter}
          />
        ))}
      </div>
    </div>
  );
}